// v10/src/components/nav/NavHistoryModal.js
//
// R7 — Modal con histórico de planes de navegación.
// Click en fila → recarga el plan en el form como template.

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Loader, AlertCircle, ClipboardList, MapPin,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import navigationService from '../../services/navigationService';

const NavHistoryModal = ({
  aircraftId,
  pilotId,
  onClose,
  onLoadAsTemplate,
  darkMode,
}) => {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await navigationService.history({
          aircraft_id: aircraftId,
          pilot_id: pilotId,
          limit: 50,
        });
        if (cancelled) return;
        setItems(r.data?.items || []);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              t('nav_history_load_error', { ns: 'admin' })
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [aircraftId, pilotId, t]);

  const handleRowClick = (item) => {
    if (onLoadAsTemplate) onLoadAsTemplate(item);
    onClose();
  };

  // ── Styles ──────────────────────────────────────────────────────
  const bg     = darkMode ? '#111827' : 'white';
  const text   = darkMode ? 'white' : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const muted  = darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const rowBg  = darkMode ? 'rgba(255,255,255,0.03)' : 'white';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 24 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 24 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: 640,
            maxHeight: '88vh',
            borderRadius: 24,
            background: bg,
            boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            border,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '18px 22px 14px',
              borderBottom: border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 13,
                  background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
                }}
              >
                <ClipboardList style={{ width: 18, height: 18, color: 'white' }} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: text }}>
                  {t('nav_history_title', { ns: 'admin' })}
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
                border: 'none',
                borderRadius: 10,
                width: 32,
                height: 32,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: sub,
              }}
            >
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>

          {/* Body */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '14px 18px 18px',
            }}
          >
            {loading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '48px 0',
                  color: sub,
                }}
              >
                <Loader style={{ width: 22, height: 22, animation: 'spin 1s linear infinite' }} />
              </div>
            ) : error ? (
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.2)',
                  color: '#ef4444',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <AlertCircle style={{ width: 14, height: 14 }} />
                {error}
              </div>
            ) : items.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '48px 16px',
                  color: muted,
                  fontSize: 14,
                }}
              >
                <ClipboardList style={{ width: 36, height: 36, opacity: 0.4, margin: '0 auto 12px' }} />
                <p style={{ margin: 0 }}>{t('nav_history_empty', { ns: 'admin' })}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map((item, i) => (
                  <HistoryRow
                    key={item.id}
                    item={item}
                    index={i}
                    onClick={() => handleRowClick(item)}
                    text={text}
                    sub={sub}
                    muted={muted}
                    rowBg={rowBg}
                    border={border}
                    darkMode={darkMode}
                    t={t}
                  />
                ))}
                {items.some(it => !Number.isFinite(Number(it?.result?.total_fuel_with_reserve_gal))) && (
                  <p style={{ fontSize: 11, color: muted, marginTop: 8, marginBottom: 0, lineHeight: 1.4 }}>
                    {t('nav_history_no_reserve_legend', { ns: 'admin' })}
                  </p>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </AnimatePresence>
  );
};

const HistoryRow = ({ item, index, onClick, text, sub, muted, rowBg, border, darkMode, t }) => {
  const result = item.result || {};
  const waypoints = Array.isArray(item.waypoints) ? item.waypoints : [];
  const routeStr = [item.origin_icao, ...waypoints, item.destination_icao]
    .filter(Boolean)
    .join(' → ');
  const dateStr = item.created_at
    ? new Date(item.created_at).toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
      onClick={onClick}
      whileHover={{ scale: 1.005 }}
      whileTap={{ scale: 0.99 }}
      style={{
        borderRadius: 14,
        border,
        background: rowBg,
        padding: '12px 14px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 12,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: darkMode ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.1)',
        }}
      >
        <MapPin style={{ width: 18, height: 18, color: '#6366f1' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontWeight: 800, fontSize: 13, color: text }}>
            {routeStr}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginTop: 3,
            fontSize: 11,
            color: sub,
            flexWrap: 'wrap',
          }}
        >
          <span>📅 {dateStr}</span>
          {item.registration && <span>✈️ {item.registration}</span>}
          {Number.isFinite(Number(result.total_distance_nm)) && (
            <span>{result.total_distance_nm} {t('nav_unit_nm', { ns: 'admin' })}</span>
          )}
          {Number.isFinite(Number(result.total_fuel_with_reserve_gal)) ? (
            <span>{result.total_fuel_with_reserve_gal} {t('nav_unit_gal', { ns: 'admin' })}</span>
          ) : Number.isFinite(Number(result.total_fuel_gal)) ? (
            <span>{result.total_fuel_gal} {t('nav_unit_gal', { ns: 'admin' })}<sup style={{ marginLeft: 2 }}>*</sup></span>
          ) : null}
          {result.wind && Number.isFinite(Number(result.wind.speed_kt)) && Number(result.wind.speed_kt) > 0 && (
            <span>💨 {Math.round(Number(result.wind.direction_deg))}° / {Math.round(Number(result.wind.speed_kt))} kt</span>
          )}
        </div>
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: darkMode ? '#a5b4fc' : '#6366f1',
          padding: '4px 10px',
          borderRadius: 99,
          background: darkMode ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.10)',
          flexShrink: 0,
        }}
      >
        {t('nav_history_use_as_template', { ns: 'admin' })}
      </div>
    </motion.div>
  );
};

export default NavHistoryModal;
